import { useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import {
  User,
  Clipboard,
  Shield,
  Users,
  Building2,
  Trophy,
  Sparkles,
  CheckCircle2,
  UserPlus,
  Rocket,
  Clock,
  BadgeCheck,
  Heart,
  Zap,
  MessageSquare,
  FileSpreadsheet,
  Smartphone,
  Calendar,
  AlertTriangle,
  Mail,
} from "lucide-react";
import heroMockup from "@/assets/new-landing/hero-mockup.jpg";
import featurePlayerPassport from "@/assets/new-landing/feature-player-passport.jpg";
import featureClubManagement from "@/assets/new-landing/feature-club-management.jpg";
import featureTrainingManagement from "@/assets/new-landing/feature-training-management.jpg";
import featureVenueBooking from "@/assets/new-landing/feature-venue-booking.jpg";
import StickyFeatureSection from "@/components/new-landing/StickyFeatureSection";
import ScrollParallax from "@/components/new-landing/ScrollParallax";
import AnimatedTimeline from "@/components/new-landing/AnimatedTimeline";
import RoleCard from "@/components/new-landing/RoleCard";
import HorizontalScrollSection from "@/components/new-landing/HorizontalScrollSection";
import OutcomesSection from "@/components/new-landing/OutcomesSection";
import InteractiveFeatureGrid from "@/components/new-landing/InteractiveFeatureGrid";
import ComparisonTable from "@/components/new-landing/ComparisonTable";
import TestimonialsSection from "@/components/new-landing/TestimonialsSection";
import VideoShowcase from "@/components/new-landing/VideoShowcase";
import HeroRotatingText from "@/components/new-landing/HeroRotatingText";
import PlayerPassportPreview from "@/components/new-landing/PlayerPassportPreview";
import ClubManagementPreview from "@/components/new-landing/ClubManagementPreview";
import TrainingManagementPreview from "@/components/new-landing/TrainingManagementPreview";
import { useScrollProgress } from "@/components/new-landing/hooks/useScrollProgress";
import { toast } from "sonner";

const IndexPage = () => {
  const { t } = useTranslation();
  const heroRef = useRef<HTMLDivElement>(null);
  const scrollProgress = useScrollProgress(heroRef);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const handleAuthRedirects = async () => {
      const errorCode = searchParams.get("error_code");
      const errorDescription = searchParams.get("error_description");
      const type = searchParams.get("type"); // signup, recovery, email_change

      // 1. Handle Errors (Expired Links)
      if (errorCode) {
        if (errorCode === "otp_expired") {
          toast.error("Link Expired", {
            description:
              "Your verification link has expired. Please sign in to request a new one.",
            duration: 6000,
          });
        } else {
          toast.error("Authentication Error", {
            description:
              errorDescription || "An error occurred during verification.",
          });
        }
        // Clean the URL parameters so they don't persist on refresh
        setSearchParams({}, { replace: true });
        return;
      }

      // 2. Handle Success States
      // Note: type is passed by Supabase in the redirect URL
      if (type === "signup") {
        toast.success("Welcome to FanJago!", {
          description:
            "Your email has been successfully verified. Let's get started!",
        });
        setSearchParams({}, { replace: true });
      }

      if (type === "email_change") {
        toast.success("Email Updated", {
          description: "Your email address has been successfully changed.",
        });
        setSearchParams({}, { replace: true });
      }
    };

    handleAuthRedirects();
  }, [searchParams, setSearchParams]);

  const scrollToSection = (id: string) => {
    const element = document.querySelector(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5 },
    },
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        name: "FanJago",
        url: "https://www.fanjago.com",
        potentialAction: {
          "@type": "SearchAction",
          target: "https://www.fanjago.com/search?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@type": "Organization",
        name: "FanJago",
        url: "https://www.fanjago.com",
        logo: "https://www.fanjago.com/favlogo-dark.png",
        sameAs: [
          "https://x.com/fanjagofootball",
          "https://www.instagram.com/fanjago_football",
          "https://www.facebook.com/61583617752484",
          "https://www.youtube.com/@Fanjago-Football",
        ],
      },
      {
        "@type": "SoftwareApplication",
        name: "FanJago",
        applicationCategory: "SportsApplication",
        operatingSystem: "Web",
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "USD",
        },
        description:
          "All-in-One Football Management Platform for clubs, coaches, players & parents.",
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.8",
          ratingCount: "1024",
        },
      },
      {
        "@type": "ItemList",
        itemListElement: [
          {
            "@type": "SiteNavigationElement",
            position: 1,
            name: "Sign Up",
            description: "Create a new FanJago account",
            url: "https://fanjago.com/auth?tab=signup",
          },
          {
            "@type": "SiteNavigationElement",
            position: 2,
            name: "Login",
            description: "Sign in to your FanJago account",
            url: "https://fanjago.com/auth",
          },
          {
            "@type": "SiteNavigationElement",
            position: 3,
            name: "Player",
            description: "For Players: Track stats and get discovered",
            url: "https://fanjago.com/player",
          },
          {
            "@type": "SiteNavigationElement",
            position: 4,
            name: "Coach",
            description: "For Coaches: Manage teams and training",
            url: "https://fanjago.com/coach",
          },
          {
            "@type": "SiteNavigationElement",
            position: 5,
            name: "Club Owner",
            description: "For Club Owners: Manage your organization",
            url: "https://fanjago.com/club-owner",
          },
          {
            "@type": "SiteNavigationElement",
            position: 6,
            name: "Parent",
            description: "For Parents: Monitor your child's progress",
            url: "https://fanjago.com/parent",
          },
          {
            "@type": "SiteNavigationElement",
            position: 7,
            name: "Venue Owner",
            description: "For Venue Owners: Manage bookings",
            url: "https://fanjago.com/venue-owner",
          },
          {
            "@type": "SiteNavigationElement",
            position: 8,
            name: "Tournament Organizer",
            description: "For Organizers: Manage tournaments",
            url: "https://fanjago.com/tournament-organizer",
          },
        ],
      },
    ],
  };

  return (
    <motion.div
      className="min-h-screen bg-background"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <SEO
        title="All-in-One Football Management"
        description="FanJago is the ultimate football management platform for clubs, coaches, players, and parents. Manage training, matches, scouting, and more."
        keywords={[
          "football management",
          "soccer club management",
          "scouting platform",
          "football training app",
          "player passport",
          "fanjago",
        ]}
        structuredData={structuredData}
      />


      {/* Hero Section - Full Screen with Scroll-Driven Text */}
      <section
        id="overview"
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden scroll-snap-section"
      >
        {/* Gradient Mesh Background */}
        <div className="absolute inset-0 -z-10 bg-gradient-mesh" />

        {/* Floating gradient orbs */}
        <div className="absolute top-20 left-[10%] w-[500px] h-[500px] rounded-full bg-accent/10 blur-[120px] animate-float-slow -z-5" />
        <div className="absolute bottom-20 right-[10%] w-[400px] h-[400px] rounded-full bg-accent/8 blur-[100px] animate-float-delayed -z-5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-accent/5 blur-[150px] -z-5" />

        {/* Subtle grid pattern overlay */}
        <div
          className="absolute inset-0 -z-5 opacity-[0.02]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, hsl(var(--foreground)) 1px, transparent 0)",
            backgroundSize: "40px 40px",
          }}
        />

        {/* Stadium Spotlight Effect */}
        <div className="absolute inset-0 stadium-spotlight -z-5" />

        {/* Animated noise texture overlay */}
        <div className="absolute inset-0 overflow-hidden -z-4">
          <div className="noise-overlay" />
        </div>

        <motion.div
          className="container mx-auto px-6 pt-40 pb-32 transition-all duration-300"
          style={{
            opacity: 1 - scrollProgress * 0.7,
            transform: `translateY(${-scrollProgress * 50}px)`,
          }}
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left side - Text content */}
            <div className="space-y-8 text-center lg:text-left">
              {/* Eyebrow text */}
              <motion.div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/30 text-accent text-sm font-medium"
                variants={fadeInUp}
              >
                <Sparkles className="w-4 h-4" />
                {t("marketing.home.hero.eyebrow", {
                  defaultValue: "For Football Academies, Clubs & Coaches",
                })}
              </motion.div>

              {/* Outcome-focused headline */}
              <motion.h1
                className="font-heading font-bold text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9] tracking-[-0.04em] text-gradient-premium"
                variants={fadeInUp}
              >
                {t("marketing.home.hero.titlePrefix", {
                  defaultValue: "Run Your Football",
                })}
                <br />
                <HeroRotatingText
                  words={[
                    t("marketing.home.hero.rotating.club", {
                      defaultValue: "club",
                    }),
                    t("marketing.home.hero.rotating.academy", {
                      defaultValue: "academy",
                    }),
                    t("marketing.home.hero.rotating.program", {
                      defaultValue: "program",
                    }),
                    t("marketing.home.hero.rotating.team", {
                      defaultValue: "team",
                    }),
                  ]}
                />
                <br className="hidden sm:block" />
                {" "}
                {t("marketing.home.hero.titleSuffix", {
                  defaultValue: "Like a Pro",
                })}
              </motion.h1>

              {/* Value-focused subheadline */}
              <motion.p
                className="text-xl sm:text-2xl md:text-3xl text-muted-foreground leading-[1.4] max-w-2xl mx-auto lg:mx-0"
                variants={fadeInUp}
              >
                {t("marketing.home.hero.subtitle", {
                  defaultValue:
                    "The all-in-one platform that replaces scattered chats, lost spreadsheets, and endless admin work.",
                })}
              </motion.p>

              {/* CTAs */}
              <motion.div
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4"
                variants={fadeInUp}
              >
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    size="lg"
                    asChild
                    className="text-lg px-8 py-6 h-auto bg-accent hover:bg-accent-soft shadow-premium-lg w-full sm:w-auto"
                  >
                    <Link to="/auth?tab=signup">
                      {t("marketing.home.hero.primaryCta", {
                        defaultValue: "Get Started Free",
                      })}
                    </Link>
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => scrollToSection("#features")}
                    className="text-lg px-8 py-6 h-auto border-border hover:bg-surface w-full sm:w-auto"
                  >
                    {t("marketing.home.hero.secondaryCta", {
                      defaultValue: "See How It Works",
                    })}
                  </Button>
                </motion.div>
              </motion.div>

              {/* CTA Reassurance */}
              <motion.div
                className="flex flex-wrap items-center justify-center lg:justify-start gap-4 text-sm text-muted-foreground"
                variants={fadeInUp}
              >
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-accent" />
                  {t("marketing.home.hero.noCreditCard", {
                    defaultValue: "No credit card required",
                  })}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-accent" />
                  {t("marketing.home.hero.fiveMinSetup", {
                    defaultValue: "5-minute setup",
                  })}
                </span>
                <span className="flex items-center gap-1.5">
                  <BadgeCheck className="w-4 h-4 text-accent" />
                  {t("marketing.home.hero.cancelAnytime", {
                    defaultValue: "Cancel anytime",
                  })}
                </span>
              </motion.div>
            </div>

            {/* Right side - Hero product visual */}
            <motion.div className="relative hidden lg:block" variants={scaleIn}>
              <div className="relative">
                {/* Green accent background card - tilted */}
                <div className="absolute -inset-4 bg-gradient-to-br from-accent via-accent to-accent-soft rounded-3xl transform rotate-3 opacity-90" />
                <div className="absolute -inset-4 bg-gradient-to-br from-accent/80 to-accent-soft/60 rounded-3xl transform -rotate-1 opacity-70" />

                {/* Product mockup */}
                <motion.div
                  className="relative rounded-2xl overflow-hidden border-2 border-white/20 shadow-2xl z-10"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <img
                    src={heroMockup}
                    alt={t("marketing.home.hero.mockupAlt", {
                      defaultValue:
                        "FanJago platform dashboard showing club management features",
                    })}
                    className="w-full h-auto"
                    loading="eager"
                  />
                </motion.div>

                {/* Floating stats badges */}
                <motion.div
                  className="absolute -left-8 top-1/4 bg-surface/95 backdrop-blur-sm border border-border/50 rounded-xl p-4 shadow-xl z-20"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">50+</p>
                      <p className="text-xs text-muted-foreground">Clubs</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="absolute -right-8 bottom-1/4 bg-surface/95 backdrop-blur-sm border border-border/50 rounded-xl p-4 shadow-xl z-20"
                  animate={{ y: [0, 10, 0] }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1,
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">10K+</p>
                      <p className="text-xs text-muted-foreground">
                        Hours Saved
                      </p>
                    </div>
                  </div>
                </motion.div>

              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Problem Statement */}
      <motion.section
        className="py-24 md:py-32 flex items-center justify-center px-6 bg-surface/30 relative overflow-hidden"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        {/* Background gradient orb - adds depth */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[350px] md:w-[700px] md:h-[450px] bg-gradient-radial from-destructive/10 via-destructive/5 to-transparent rounded-full blur-3xl" />

        {/* Floating chaos icons */}
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute left-[8%] top-[20%]"
            animate={{ y: [0, -12, 0], rotate: [-8, 4, -8] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          >
            <MessageSquare className="w-10 h-10 md:w-14 md:h-14 text-destructive/25" />
          </motion.div>
          <motion.div
            className="absolute right-[12%] top-[25%]"
            animate={{ y: [0, 10, 0], rotate: [6, -6, 6] }}
            transition={{
              duration: 4.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0.5,
            }}
          >
            <FileSpreadsheet className="w-8 h-8 md:w-12 md:h-12 text-muted-foreground/30" />
          </motion.div>
          <motion.div
            className="absolute left-[15%] bottom-[25%]"
            animate={{ y: [0, 8, 0], rotate: [4, -8, 4] }}
            transition={{
              duration: 5.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
          >
            <Smartphone className="w-8 h-8 md:w-11 md:h-11 text-muted-foreground/25" />
          </motion.div>
          <motion.div
            className="absolute right-[10%] bottom-[30%]"
            animate={{ y: [0, -10, 0], rotate: [-5, 7, -5] }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0.8,
            }}
          >
            <Calendar className="w-9 h-9 md:w-12 md:h-12 text-destructive/20" />
          </motion.div>
          <motion.div
            className="absolute left-[25%] top-[35%]"
            animate={{ y: [0, 6, 0], rotate: [3, -5, 3] }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1.5,
            }}
          >
            <AlertTriangle className="w-7 h-7 md:w-10 md:h-10 text-orange-500/20" />
          </motion.div>
          <motion.div
            className="absolute right-[22%] top-[40%]"
            animate={{ y: [0, -8, 0], rotate: [-4, 6, -4] }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2,
            }}
          >
            <Mail className="w-8 h-8 md:w-11 md:h-11 text-muted-foreground/25" />
          </motion.div>
        </div>

        <div className="container mx-auto max-w-5xl text-center space-y-12 relative z-10">
          <motion.h2
            className="font-heading font-bold text-5xl md:text-6xl lg:text-8xl text-foreground leading-[1.1] tracking-[-0.03em]"
            variants={fadeInUp}
          >
            {t("marketing.home.problem.title", {
              defaultValue: "Managing football shouldn't be",
            })}{" "}
            <span className="relative inline-block text-destructive">
              {t("marketing.home.problem.chaotic", { defaultValue: "chaotic" })}
              <motion.span
                className="absolute left-0 top-1/2 w-full h-1 md:h-1.5 bg-accent origin-left -rotate-2"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{
                  delay: 0.8,
                  duration: 0.6,
                  ease: "easeOut",
                }}
              />
            </span>
          </motion.h2>
          <motion.p
            className="text-2xl md:text-3xl lg:text-4xl text-muted-foreground leading-[1.5] max-w-4xl mx-auto"
            variants={fadeInUp}
          >
            {t("marketing.home.problem.subtitle", {
              defaultValue:
                "Scattered chats, lost data. There's a better way.",
            })}
          </motion.p>
        </div>
      </motion.section>

      {/* Outcomes Section */}
      <OutcomesSection />

      {/* Stats Showcase ("Growing Every Day") */}
      {/* <StatsShowcase /> */}

      {/* Platform Section - Image Carousel */}
      <StickyFeatureSection
        id="features"
        mode="slideshow"
        use3D={false}
        productImages={[
          featurePlayerPassport,
          featureClubManagement,
          featureTrainingManagement,
          // featureVenueBooking, // Smart Payment Tracking (disabled)
        ]}
        title="One Platform, Everything Connected"
        customComponents={[
          <PlayerPassportPreview key="passport" />,
          <ClubManagementPreview key="club-management" />,
          <TrainingManagementPreview key="training-management" />,
          // null, // Smart Payment Tracking (disabled)
        ]}
        features={[
          {
            title: "Digital Player Passport",
            description:
              "Track every match, stat, and achievement in one place that follows the player throughout their journey.",
            icon: <CheckCircle2 className="w-8 h-8" />,
          },
          {
            title: "Club Management Made Simple",
            description:
              "Organize teams, programs, registrations, and payments without the spreadsheet headaches.",
            icon: <CheckCircle2 className="w-8 h-8" />,
          },
          {
            title: "Training Management Made Easy",
            description:
              "Plan, schedule, and manage training sessions with a visual calendar that keeps everyone organized.",
            icon: <CheckCircle2 className="w-8 h-8" />,
          },
          // {
          //   title: "Smart Payment Tracking",
          //   description:
          //     "Track fees and installments so nothing gets lost in chat messages or email threads.",
          //   icon: <CheckCircle2 className="w-8 h-8" />,
          // },
        ]}
      />

      {/* AI Camera Section - 3D Showcase */}
      {/*
      <StickyFeatureSection
        use3D={false}
        mode="slideshow"
        productImages={[
          aiCameraInstalled,
          aiCameraFront,
          aiCameraApp,
          aiCameraSide,
        ]}
        title="AI-Powered Match Recording"
        features={[
          {
            title: "Automated Match Recording",
            description:
              "Captures every moment of the game automatically without manual operation.",
            icon: <Video className="w-12 h-12" />,
          },
          {
            title: "Intelligent Player Tracking",
            description:
              "AI follows the action across the field, keeping every player in frame.",
            icon: <Crosshair className="w-12 h-12" />,
          },
          {
            title: "Auto Highlight Detection",
            description:
              "Automatically identifies goals, saves, and key moments in real-time.",
            icon: <Sparkles className="w-12 h-12" />,
          },
          {
            title: "Instant Clip Generation",
            description:
              "Creates shareable highlights for each player's digital passport.",
            icon: <Share2 className="w-12 h-12" />,
          },
        ]}
      />
      */}

      {/* Interactive Feature Grid */}
      <InteractiveFeatureGrid />

      {/* Horizontal Scroll - Roles (Apple Carousel Style) */}
      <HorizontalScrollSection
        id="roles"
        title="Who Are You in the Game?"
        subtitle="Tap your role to discover how FanJago helps you"
        className="bg-background"
      >
        <RoleCard
          name="Fan"
          icon={Heart}
          tagline="Follow your club. Support your players."
          description="Live match updates, schedules, highlights, and community features — all in one place."
          ctaLabel="I'm a Fan"
          link="/fan"
        />
        <RoleCard
          name="Player"
          icon={User}
          tagline="Track your journey, stats and get discovered."
          description="Keep all your matches, stats and highlights in one digital player passport."
          ctaLabel="I'm a Player"
          link="/player"
        />
        <RoleCard
          name="Coach"
          icon={Clipboard}
          tagline="Plan sessions, manage teams and monitor progress."
          description="Set up trainings, lineups and season plans while tracking attendance and performance."
          ctaLabel="I'm a Coach"
          link="/coach"
        />
        <RoleCard
          name="Club Owner"
          icon={Shield}
          tagline="Run your club like a pro."
          description="Programs, staff, players, fees and communication – managed like a real football organisation."
          ctaLabel="I manage a Club"
          link="/club-owner"
        />
        <RoleCard
          name="Parent"
          icon={Users}
          tagline="Stay close to your child's football journey."
          description="View schedules, pay fees and follow progress without hunting through chat history."
          ctaLabel="I'm a Parent"
          link="/parent"
        />
        <RoleCard
          name="Venue Owner"
          icon={Building2}
          tagline="Make your pitch easy to book."
          description="Manage time slots, bookings and payments across your fields in one place."
          ctaLabel="I own a Venue"
          link="/venue-owner"
          comingSoon={true}
        />
        <RoleCard
          name="Tournament Organizer"
          icon={Trophy}
          tagline="Run your tournament like a pro."
          description="Fixtures, results, standings and registrations — all in one place."
          ctaLabel="I organize Tournaments"
          link="/tournament-organizer"
          comingSoon={true}
        />
      </HorizontalScrollSection>

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* Video Showcase */}
      <VideoShowcase />

      {/* Comparison Table */}
      <ComparisonTable />

      {/* Pricing Section ("Simple, Transparent Pricing") */}
      {/* <PricingSection /> */}

      {/* Future Vision Section */}
      <motion.section
        className="py-20 sm:py-24 md:py-32 px-4 sm:px-6 lg:px-8 bg-background relative overflow-hidden"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <div className="absolute inset-0 opacity-10">
          <motion.div
            className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-accent-soft blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.15, 0.1],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-accent blur-3xl"
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.1, 0.2, 0.1],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>

        <div className="container mx-auto max-w-5xl relative z-10">
          <div className="text-center mb-12 sm:mb-16">
            <motion.div
              className="inline-flex items-center gap-2 sm:gap-3 bg-accent/20 text-accent-soft px-4 sm:px-6 py-2 sm:py-3 rounded-full mb-6 sm:mb-8 backdrop-blur-sm border border-accent/30"
              variants={scaleIn}
            >
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="text-base sm:text-lg font-semibold">
                Coming Soon
              </span>
            </motion.div>
            <motion.h2
              className="font-heading font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl mb-6 sm:mb-8 text-foreground px-4"
              variants={fadeInUp}
            >
              Built for the Future of Football
            </motion.h2>
            <motion.p
              className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-muted-foreground leading-relaxed max-w-3xl mx-auto px-4"
              variants={fadeInUp}
            >
              A smarter football ecosystem powered by AI and video.
            </motion.p>
          </div>
          <motion.div
            className="grid sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8 mt-12 sm:mt-16 md:mt-20"
            variants={staggerContainer}
          >
            <motion.div
              className="flex items-start gap-3 sm:gap-4 bg-surface/50 backdrop-blur-sm p-6 sm:p-8 rounded-xl border border-border/50"
              variants={fadeInUp}
              whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
            >
              <CheckCircle2 className="w-6 h-6 sm:w-8 sm:h-8 text-accent-soft flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-heading font-bold text-xl sm:text-2xl mb-2 sm:mb-3 text-foreground">
                  AI Lineup Suggestions
                </h3>
                <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                  Smart recommendations based on performance data.
                </p>
              </div>
            </motion.div>
            <motion.div
              className="flex items-start gap-3 sm:gap-4 bg-surface/50 backdrop-blur-sm p-6 sm:p-8 rounded-xl border border-border/50"
              variants={fadeInUp}
              whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
            >
              <CheckCircle2 className="w-6 h-6 sm:w-8 sm:h-8 text-accent-soft flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-heading font-bold text-xl sm:text-2xl mb-2 sm:mb-3 text-foreground">
                  AI Scouting
                </h3>
                <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                  Intelligent recommendations based on player trends.
                </p>
              </div>
            </motion.div>
            <motion.div
              className="flex items-start gap-3 sm:gap-4 bg-surface/50 backdrop-blur-sm p-6 sm:p-8 rounded-xl border border-border/50"
              variants={fadeInUp}
              whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
            >
              <CheckCircle2 className="w-6 h-6 sm:w-8 sm:h-8 text-accent-soft flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-heading font-bold text-xl sm:text-2xl mb-2 sm:mb-3 text-foreground">
                  AI Camera Integration
                </h3>
                <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                  Automated match tagging and highlight creation.
                </p>
              </div>
            </motion.div>
            <motion.div
              className="flex items-start gap-3 sm:gap-4 bg-surface/50 backdrop-blur-sm p-6 sm:p-8 rounded-xl border border-border/50"
              variants={fadeInUp}
              whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
            >
              <CheckCircle2 className="w-6 h-6 sm:w-8 sm:h-8 text-accent-soft flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-heading font-bold text-xl sm:text-2xl mb-2 sm:mb-3 text-foreground">
                  Auto Highlights
                </h3>
                <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                  Key clips delivered to each player's passport.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Animated Timeline - How It Works */}
      <AnimatedTimeline
        id="how_it_works"
        title="How It Works"
        steps={[
          {
            number: "1",
            title: "Create your account",
            description:
              "Sign up with your email or phone. Secure your login and you're ready to go.",
            icon: <UserPlus className="w-8 h-8" />,
          },
          {
            number: "2",
            title: "Choose your role",
            description:
              "Start as a player, coach, club owner, parent, or venue owner. You can add more roles anytime.",
            icon: <Shield className="w-8 h-8" />,
          },
          {
            number: "3",
            title: "Set up and start",
            description:
              "Complete your profile, add your teams or children, connect to your club and start using your tools immediately.",
            icon: <Rocket className="w-8 h-8" />,
          },
        ]}
      />

      {/* Landing FAQ Section ("Got Questions?") */}
      {/* <LandingFAQ /> */}

      {/* Final CTA - Full Screen */}
      <motion.section
        className="min-h-screen flex items-center justify-center px-6 bg-surface/30 relative overflow-hidden scroll-snap-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-accent/10 rounded-full blur-3xl"
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ duration: 15, repeat: Infinity }}
          />
        </div>

        <div className="container mx-auto max-w-5xl relative z-10 text-center space-y-10">
          <motion.h2
            className="font-heading font-bold text-5xl md:text-6xl lg:text-7xl text-foreground"
            variants={fadeInUp}
          >
            Join 50+ Clubs Already <br className="hidden md:block" />
            <span className="text-accent">Saving Time & Staying Organized</span>
          </motion.h2>
          <motion.p
            className="text-2xl md:text-3xl text-muted-foreground max-w-3xl mx-auto leading-relaxed"
            variants={fadeInUp}
          >
            Bring your club, players, coaches, and parents into one system.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center pt-4"
            variants={fadeInUp}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                size="lg"
                asChild
                className="text-xl px-12 py-8 h-auto bg-accent hover:bg-accent-soft shadow-lg shadow-accent/50"
              >
                <Link to="/auth?tab=signup">Get Started Free</Link>
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                size="lg"
                variant="outline"
                onClick={() => scrollToSection("#features")}
                className="text-xl px-12 py-8 h-auto border-border hover:bg-surface"
              >
                Explore Features
              </Button>
            </motion.div>
          </motion.div>

          <motion.div
            className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground pt-4"
            variants={fadeInUp}
          >
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-accent" />
              No credit card required
            </span>
            <span className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-accent" />
              5-minute onboarding
            </span>
            <span className="flex items-center gap-2">
              <BadgeCheck className="w-5 h-5 text-accent" />
              Cancel anytime
            </span>
          </motion.div>

          <motion.div
            className="flex flex-wrap items-center justify-center gap-8 pt-8 text-muted-foreground"
            variants={fadeInUp}
          >
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-accent" />
              <span>50+ Clubs</span>
            </div>
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-accent" />
              <span>1,000+ Players</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-accent" />
              <span>10,000+ Hours Saved</span>
            </div>
          </motion.div>
        </div>
      </motion.section>
    </motion.div>
  );
};

export default IndexPage;
